import { Button as ButtonA } from 'antd';
import cn from 'classnames';
import { type MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type NavigateOptions, useNavigate } from 'react-router';
import styles from './button.module.css';
import { useGetButtonSettings } from './hooks';

export type ButtonProperties = {
  navigate?: ButtonNavigateProperties;
  type?: ButtonType;
} & Omit<React.ComponentProps<typeof ButtonA>, 'href' | 'type'>;

export type ButtonType =
  | 'link-inverted'
  | 'link-primary'
  | 'link-secondary'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'text'
  | 'vertical';

type ButtonNavigateProperties =
  | {
      external: true;
      to: string;
    }
  | {
      external?: false;
      options?: NavigateOptions;
      to: string;
    };

const Button = ({
  'aria-label': ariaLabel,
  children,
  className,
  danger,
  navigate,
  onClick,
  type = 'tertiary',
  ...rest
}: ButtonProperties) => {
  const { t } = useTranslation();
  const onNavigate = useNavigate();
  const { color, variant } = useGetButtonSettings({ danger, type });

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLElement>) => {
      onClick?.(event);

      if (navigate && !navigate.external) {
        event.preventDefault();
        await onNavigate(navigate.to, navigate.options);
      }
    },
    [navigate, onNavigate, onClick],
  );

  return (
    <ButtonA
      aria-label={ariaLabel ? t(ariaLabel) : ariaLabel}
      className={cn(className, styles.btn, {
        [styles['btn--link-inverted'] ?? '']: type === 'link-inverted',
        [styles['btn--vertical'] ?? '']: type === 'vertical',
      })}
      color={color}
      href={navigate?.to}
      onClick={handleClick}
      size="large"
      variant={variant}
      {...rest}
    >
      {typeof children === 'string' ? t(children) : children}
    </ButtonA>
  );
};

export default Button;
